import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import cveService from '../../services/cveService';

export const fetchCveSummary = createAsyncThunk(
  'cve/fetchSummary',
  async (_, { rejectWithValue, signal }) => {
    try {
      const res = await cveService.getSummary({ signal });
      return res?.data ?? null;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || 'Request failed');
    }
  }
);

export const fetchCveEpssDistribution = createAsyncThunk(
  'cve/fetchEpssDistribution',
  async ({ epssRange, kevOnly } = {}, { rejectWithValue, signal }) => {
    try {
      const res = await cveService.getEpssDistribution({ epssRange, kevOnly, signal });
      return res?.data ?? null;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || 'Request failed');
    }
  }
);

export const fetchCveVendors = createAsyncThunk(
  'cve/fetchVendors',
  async (_, { rejectWithValue, signal }) => {
    try {
      const res = await cveService.getVendors({ signal });
      return Array.isArray(res?.data) ? res.data : [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || 'Request failed');
    }
  }
);

export const fetchCveProducts = createAsyncThunk(
  'cve/fetchProducts',
  async (_, { rejectWithValue, signal }) => {
    try {
      const res = await cveService.getProducts({ signal });
      return Array.isArray(res?.data) ? res.data : [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || 'Request failed');
    }
  }
);

export const fetchCveTopProducts = createAsyncThunk(
  'cve/fetchTopProducts',
  async (_, { rejectWithValue, signal }) => {
    try {
      const res = await cveService.getTopProducts({ signal });
      return Array.isArray(res?.data) ? res.data : [];
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || 'Request failed');
    }
  }
);

export const fetchCveTable = createAsyncThunk(
  'cve/fetchTable',
  async (
    { search, epssRange, vendor, product, kevOnly, page = 0, size = 10 } = {},
    { rejectWithValue, signal }
  ) => {
    try {
      const res = await cveService.getTable({
        search,
        epssRange,
        vendor,
        product,
        kevOnly,
        page,
        size,
        signal,
      });
      return res?.data ?? null;
    } catch (err) {
      return rejectWithValue(err?.response?.data || err?.message || 'Request failed');
    }
  }
);

const idle = { data: null, loading: false, error: null };
const idleList = { data: [], loading: false, error: null };

const cveSlice = createSlice({
  name: 'cve',
  initialState: {
    summary: { ...idle },
    epssDistribution: { ...idle },
    vendors: { ...idleList },
    products: { ...idleList },
    topProducts: { ...idleList },
    table: { ...idle },
  },
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCveSummary.pending, (state) => {
        state.summary.loading = true;
        state.summary.error = null;
      })
      .addCase(fetchCveSummary.fulfilled, (state, action) => {
        state.summary = { data: action.payload, loading: false, error: null };
      })
      .addCase(fetchCveSummary.rejected, (state, action) => {
        state.summary.loading = false;
        state.summary.error = action.payload || 'Request failed';
      })
      .addCase(fetchCveEpssDistribution.pending, (state) => {
        state.epssDistribution.loading = true;
        state.epssDistribution.error = null;
      })
      .addCase(fetchCveEpssDistribution.fulfilled, (state, action) => {
        state.epssDistribution = { data: action.payload, loading: false, error: null };
      })
      .addCase(fetchCveEpssDistribution.rejected, (state, action) => {
        state.epssDistribution.loading = false;
        state.epssDistribution.error = action.payload || 'Request failed';
      })
      .addCase(fetchCveVendors.pending, (state) => {
        state.vendors.loading = true;
        state.vendors.error = null;
      })
      .addCase(fetchCveVendors.fulfilled, (state, action) => {
        state.vendors = { data: action.payload, loading: false, error: null };
      })
      .addCase(fetchCveVendors.rejected, (state, action) => {
        state.vendors.loading = false;
        state.vendors.error = action.payload || 'Request failed';
      })
      .addCase(fetchCveProducts.pending, (state) => {
        state.products.loading = true;
        state.products.error = null;
      })
      .addCase(fetchCveProducts.fulfilled, (state, action) => {
        state.products = { data: action.payload, loading: false, error: null };
      })
      .addCase(fetchCveProducts.rejected, (state, action) => {
        state.products.loading = false;
        state.products.error = action.payload || 'Request failed';
      })
      .addCase(fetchCveTopProducts.pending, (state) => {
        state.topProducts.loading = true;
        state.topProducts.error = null;
      })
      .addCase(fetchCveTopProducts.fulfilled, (state, action) => {
        state.topProducts = { data: action.payload, loading: false, error: null };
      })
      .addCase(fetchCveTopProducts.rejected, (state, action) => {
        state.topProducts.loading = false;
        state.topProducts.error = action.payload || 'Request failed';
      })
      .addCase(fetchCveTable.pending, (state) => {
        state.table.loading = true;
        state.table.error = null;
      })
      .addCase(fetchCveTable.fulfilled, (state, action) => {
        state.table = { data: action.payload, loading: false, error: null };
      })
      .addCase(fetchCveTable.rejected, (state, action) => {
        state.table.loading = false;
        state.table.error = action.payload || 'Request failed';
      });
  },
});

export default cveSlice.reducer;
